<!--
  Temporary refresher (contract wizard simplification):
  - Internal field editing is disabled via VCfieldEditingDisabled: true.
  - Venue/VC sign toggles are hidden while this temporary mode is on.
  - Add Field is hidden for Venue/VenueCrew steps, but still available on Client step.
  - Venue/VenueCrew required-field validation is bypassed in temporary mode.
  - Step-1 text is temporarily changed to "Add Contract File (PDF only)".

  Revert when full workflow is needed:
  - Set VCfieldEditingDisabled to false in this file.
-->
<template>
  <transition name="cw-fade">
    <div v-if="visible" class="cw-overlay">

      <!-- ── Header ───────────────────────────────────────────────── -->
      <div class="cw-header">
        <button
          type="button"
          class="cw-close"
          aria-label="Close"
          @click="$emit('update:visible', false)"
        >
          &times;
        </button>
        <h2 class="cw-title">Set Up Contract</h2>
        <p v-if="venueName || clientName" class="cw-subtitle">
          Between {{ venueName }}<template v-if="clientName"> &amp; {{ clientName }}</template>
        </p>
      </div>

      <!-- ── Notification bar ──────────────────────────────────────── -->
      <div
        v-if="notification.show"
        class="cw-notification"
        :class="'cw-notification--' + notification.type"
      >
        <i :class="notificationIcon"></i>
        {{ notification.message }}
        <button class="cw-notification-close" @click="notification.show = false">×</button>
      </div>

      <!-- ── Scrollable content ─────────────────────────────────────── -->
      <div class="cw-content">

        <!-- Venue selector (step 0, no fixed venueId) -->
        <div v-if="currentStep === 0 && !venueId && availableVenues.length" class="cw-venue-select">
          <select
            class="form-control"
            style="max-width:320px;margin:0 auto 16px"
            v-model="resolvedVenueId"
          >
            <option :value="null" disabled>Select a venue…</option>
            <option v-for="v in availableVenues" :key="v.id" :value="v.id">{{ v.name }}</option>
          </select>
        </div>

        <!-- Add Venue Fields -->
        <div v-show="currentParty === 'venue' && !isSignStep">
          <pdf-field-editor
            ref="venueEditor"
            party="venue"
            :existing-fields="[]"
            :initial-pdf-bytes="pdfBytes || existingPdfBytes"
            :initial-file="steps[0].party === 'venue' ? initialPdfFile : null"
            :selected-field-type="selectedFieldType"
            @pdf-loaded="onPdfLoaded"
            @fields-updated="onVenueFieldsUpdated"
            @field-placed="onFieldPlaced"
            @duplicate-field="onDuplicateField"
            @duplicate-label="onDuplicateLabel"
          />
        </div>

        <!-- Add VenueCrew Fields -->
        <template v-if="vcSignRequired">
          <div v-show="currentParty === 'venuecrew' && !isSignStep">
            <pdf-field-editor
              ref="vcEditor"
              party="venuecrew"
              :existing-fields="steps[0].party === 'venue' ? venueFields : []"
              :initial-pdf-bytes="pdfBytes || existingPdfBytes"
              :initial-file="steps[0].party === 'venuecrew' ? initialPdfFile : null"
              :selected-field-type="selectedFieldType"
              @pdf-loaded="onPdfLoaded"
              @fields-updated="onVcFieldsUpdated"
              @field-placed="onFieldPlaced"
              @duplicate-field="onDuplicateField"
              @duplicate-label="onDuplicateLabel"
            />
          </div>
        </template>

        <!-- Add Client Fields -->
        <div v-show="currentParty === 'client' && !isSignStep">
          <pdf-field-editor
            ref="clientEditor"
            party="client"
            :existing-fields="[...venueFields, ...vcFields]"
            :initial-pdf-bytes="pdfBytes"
            :selected-field-type="selectedFieldType"
            @field-placed="onFieldPlaced"
            @fields-updated="onClientFieldsUpdated"
            @duplicate-field="onDuplicateField"
            @duplicate-label="onDuplicateLabel"
          />
        </div>

        <!-- Sign Now -->
        <div v-if="isSignStep">
          <pdf-field-editor
            ref="signEditor"
            party="venue"
            mode="sign"
            :existing-fields="allFields"
            :initial-pdf-bytes="pdfBytes"
            :selected-field-type="null"
            :signable-parties="['venuecrew']"
            @sign-field="onSignField"
            @fill-field="onFillField"
            @clear-sign="onClearSign"
          />
        </div>

        <!-- Sign field dropdown -->
        <div
          v-if="signDropdown.visible"
          class="cw-sign-dropdown"
          :class="{ 'cw-sign-dropdown--flipped': signDropdown.flipped }"
          :style="{ left: signDropdown.x + 'px', top: signDropdown.y + 'px' }"
          v-click-outside="closeSignDropdown"
        >
          <button class="cw-sign-option" @click="openSignModal('upload')">
            <span class="cw-sign-option-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 16V8M12 8l-4 4M12 8l4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M4 20h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" stroke-width="1.6" opacity="0.18"/>
              </svg>
            </span>
            <span class="cw-sign-option-label">Upload image</span>
            <span class="cw-sign-option-sep"></span>
          </button>
          <button class="cw-sign-option" @click="openSignModal('draw')">
            <span class="cw-sign-option-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M3 17c2.5-4.5 5-7 7-7 1.3 0 2 1.8 3.2 1.8 1.5 0 3.3-2.8 5.3-4.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M15.5 7l2 2-1.5 1.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="3" y1="20" x2="21" y2="20" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" opacity="0.5"/>
              </svg>
            </span>
            <span class="cw-sign-option-label">Draw signature</span>
            <span class="cw-sign-option-sep"></span>
          </button>
          <button class="cw-sign-option" @click="openSignModal('type')">
            <span class="cw-sign-option-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" stroke-width="1.6"/>
                <path d="M8 8h8M12 8v9M9.5 17h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="cw-sign-option-label">Type signature</span>
          </button>
        </div>

      </div><!-- /.cw-content -->

      <!-- ── Footer ───────────────────────────────────────────────── -->
      <div class="cw-footer">
        <div class="cw-footer-left">
          <button class="cw-back-btn" @click="handleBack" :disabled="saving">
            <i class="fa fa-arrow-left"></i>
          </button>
          <!-- Step toast -->
          <transition name="cw-toast-fade">
            <div v-if="stepToast" class="cw-step-toast">
              <i class="fa fa-check-circle"></i> {{ stepToast }}
            </div>
          </transition>
          <!-- Error toast -->
          <transition name="cw-toast-fade">
            <div v-if="errorToast" class="cw-step-toast cw-step-toast--error">
              <i class="fa fa-exclamation-triangle"></i> {{ errorToast }}
            </div>
          </transition>
          <div class="cw-step-info" :class="{ 'cw-step-info--pulse': stepPulse }">
            <span class="cw-step-label">STEP {{ currentStep + 1 }}/{{ steps.length }}</span>
            <div class="cw-step-title">{{ steps[currentStep].title }}</div>
            <div class="cw-step-sub">{{ steps[currentStep].subtitle }}</div>
          </div>
          <div v-if="currentStep === 0 && !VCfieldEditingDisabled" class="cw-party-options">
            <label class="cw-party-checkbox">
              <input type="checkbox" v-model="venueSignRequired" />
              <span>Venue Sign Required</span>
            </label>
            <label class="cw-party-checkbox">
              <input type="checkbox" v-model="vcSignRequired" />
              <span>VC Sign Required</span>
            </label>
          </div>
        </div>

        <div class="cw-footer-right">
          <!-- Change Contract File — step 1 only -->
          <button
            v-if="currentStep === 0"
            class="btn cw-btn-replace"
            @click="$emit('replace-contract')"
            :disabled="saving"
          >Change Contract File</button>

          <!-- Add Field — all field editing steps -->
          <div v-if="!isSignStep && !isCurrentStepFieldEditingDisabled" class="cw-add-field-wrap" v-click-outside="closeAddFieldMenu">
            <button
              class="btn cw-btn-add-field"
              :class="{ 'cw-btn-add-field--active': showAddFieldMenu || !!selectedFieldType }"
              @click.stop="toggleAddFieldMenu"
              :disabled="saving || (currentStep === 0 && currentParty === 'venue' && !venueSignRequired)"
            >
              <i class="fa fa-plus"></i>
              {{ addFieldButtonLabel }}
            </button>
            <div v-if="showAddFieldMenu" class="cw-field-menu">
              <button
                v-for="ft in currentFieldTypes()"
                :key="ft.type"
                class="cw-field-menu-item"
                :class="{
                  'cw-field-menu-item--active': selectedFieldType === ft.type,
                  'cw-field-menu-item--used': isFieldTypeUsed(ft.type),
                }"
                :disabled="isFieldTypeUsed(ft.type)"
                @click.stop="selectFieldType(ft.type)"
              >
                <span class="cw-field-menu-icon" v-html="fieldTypeIcon(ft.type)"></span>
                {{ ft.label }}
              </button>

              <!-- Add initials to all pages -->
              <div class="cw-field-menu-divider"></div>
              <button
                class="cw-field-menu-item"
                :class="{ 'cw-field-menu-item--active': showInitialsMenu }"
                :disabled="!pdfBytes"
                @click.stop="toggleInitialsMenu"
              >
                <span class="cw-field-menu-icon" v-html="fieldTypeIcon('initials')"></span>
                Add initials to all pages
              </button>
              <div v-if="showInitialsMenu" class="cw-initials-submenu">
                <p class="cw-initials-menu-label">Place on all pages at:</p>
                <div class="cw-initials-grid">
                  <button class="cw-initials-opt" @click="placeInitialsOnAllPages('top-left')">
                    <span class="cw-initials-icon">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="2" y="2" width="28" height="28" rx="3" stroke="#9ca3af" stroke-width="1.4" fill="none"/><rect x="3" y="3" width="9" height="6" rx="1.5" fill="#4c1d95" opacity="0.85"/></svg>
                    </span>
                    <span>Top Left</span>
                  </button>
                  <button class="cw-initials-opt" @click="placeInitialsOnAllPages('top-right')">
                    <span class="cw-initials-icon">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="-2" y="2" width="28" height="28" rx="3" stroke="#9ca3af" stroke-width="1.4" fill="none"/><rect x="16" y="3" width="9" height="6" rx="1.5" fill="#4c1d95" opacity="0.85"/></svg>
                    </span>
                    <span>Top Right</span>
                  </button>
                  <button class="cw-initials-opt" @click="placeInitialsOnAllPages('bottom-left')">
                    <span class="cw-initials-icon">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="2" y="-2" width="28" height="28" rx="3" stroke="#9ca3af" stroke-width="1.4" fill="none"/><rect x="3" y="19" width="9" height="6" rx="1.5" fill="#4c1d95" opacity="0.85"/></svg>
                    </span>
                    <span>Bottom Left</span>
                  </button>
                  <button class="cw-initials-opt" @click="placeInitialsOnAllPages('bottom-right')">
                    <span class="cw-initials-icon">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="-2" y="-2" width="28" height="28" rx="3" stroke="#9ca3af" stroke-width="1.4" fill="none"/><rect x="16" y="19" width="9" height="6" rx="1.5" fill="#4c1d95" opacity="0.85"/></svg>
                    </span>
                    <span>Bottom Right</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Continue — all field editing steps -->
          <button
            v-if="!isSignStep"
            class="btn cw-btn-continue"
            @click="goNext"
            :disabled="saving || !canProceed"
          >
            <span v-if="saving"><i class="fa fa-spinner fa-spin"></i> Saving…</span>
            <span v-else-if="currentStep === 0">Add Fields <i class="fa fa-arrow-right"></i></span>
            <span v-else-if="currentStep === 1">Save &amp; Continue <i class="fa fa-arrow-right"></i></span>
            <span v-else>Continue <i class="fa fa-arrow-right"></i></span>
          </button>

          <!-- Sign step: Sign Contract + Complete & Send -->
          <template v-if="isSignStep">
            <button
              v-if="vcSignRequired && hasVcSignatureFields"
              class="btn cw-btn-sign cw-btn-sign--vc"
              @click="scrollToVcSignField"
              :disabled="saving"
            >
              <i class="fa fa-pencil-square-o"></i> Sign Contract
            </button>
            <button
              class="btn cw-btn-continue"
              @click="completeSend"
              :disabled="saving"
            >
              <span v-if="saving"><i class="fa fa-spinner fa-spin"></i> Sending…</span>
              <span v-else><i class="fa fa-paper-plane"></i> Send to Client</span>
            </button>
          </template>
        </div>
      </div><!-- /.cw-footer -->

      <!-- ── Venue Sign Modal ───────────────────────────────────────── -->
      <el-dialog
        :visible.sync="showVenueSignModal"
        width="500px"
        :append-to-body="true"
        :show-close="false"
        custom-class="vsig-modal"
        @open="onSignModalOpen"
      >
        <button class="vsig-close" @click="showVenueSignModal = false" aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="#6b7280" stroke-width="1.6" stroke-linecap="round"/></svg>
        </button>

        <div class="vsig-header">
          <h2 class="vsig-title">{{ signModalTitle }}</h2>
          <p class="vsig-subtitle">{{ signModalSubtitle }}</p>
        </div>

        <!-- DRAW -->
        <div v-if="signTab === 'draw'" class="vsig-body">
          <div class="vsig-canvas-wrap">
            <span v-if="sigPadIsEmpty" class="vsig-canvas-hint">Sign here</span>
            <canvas ref="sigCanvas" class="vsig-canvas" :style="sigPreviewStyle"></canvas>
          </div>
        </div>

        <!-- TYPE -->
        <div v-if="signTab === 'type'" class="vsig-body">
          <div class="vsig-type-input-wrap">
            <input
              v-model="typedSignature"
              type="text"
              class="vsig-type-input"
              :style="{ fontFamily: typedSigFont }"
              placeholder="John Smith"
              maxlength="80"
            />
            <span class="vsig-type-edit">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M11 2l3 3-9 9H2v-3l9-9z" stroke="#9ca3af" stroke-width="1.4" stroke-linejoin="round"/></svg>
            </span>
          </div>
          <div class="vsig-font-chips">
            <button
              v-for="font in signatureFonts"
              :key="font.family"
              class="vsig-font-chip"
              :class="{ 'vsig-font-chip--active': typedSigFont === font.family }"
              :style="{ fontFamily: font.family }"
              @click="typedSigFont = font.family"
            >{{ typedSignature || 'John Smith' }}</button>
          </div>
        </div>

        <!-- UPLOAD -->
        <div v-if="signTab === 'upload'" class="vsig-body">
          <input ref="sigImageInput" type="file" accept="image/jpeg,image/png,image/gif" style="display:none" @change="onSigImageUpload" />
          <template v-if="!sigImageDataUrl">
            <div class="vsig-dropzone" @click="$refs.sigImageInput.click()">
              <div class="vsig-dropzone-icon">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M14 19V6M14 19l-5-5M14 19l5-5" stroke="#9ca3af" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 23h18" stroke="#9ca3af" stroke-width="1.8" stroke-linecap="round"/></svg>
              </div>
              <p class="vsig-dropzone-label">Click to upload or drag and drop</p>
              <p class="vsig-dropzone-hint">PNG, JPG up to 10MB</p>
            </div>
          </template>
          <template v-else>
            <div
              class="vsig-upload-preview"
              :class="{ 'vsig-panning': sigPanDrag }"
              :style="sigPreviewStyle"
              @mousedown="onSigPanStart"
            >
              <canvas class="vsig-upload-canvas"></canvas>
            </div>
            <div class="vsig-zoom-row">
              <span class="vsig-zoom-label">Zoom:</span>
              <input
                type="range"
                min="1"
                max="4"
                step="0.05"
                v-model.number="sigImageZoom"
                class="vsig-zoom-slider"
                :style="{ '--vsig-zoom-pct': sigZoomPct + '%' }"
              />
            </div>
          </template>
        </div>

        <div class="vsig-footer">
          <button
            v-if="signTab === 'draw'"
            class="vsig-link-btn"
            @click="clearSignaturePad"
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style="margin-right:6px;vertical-align:-2px"><path d="M2 8a6 6 0 1 0 1.8-4.3M2 2v3h3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            DRAW AGAIN
          </button>
          <button
            v-else-if="signTab === 'upload' && sigImageDataUrl"
            class="vsig-link-btn"
            @click="removeSigImage"
          >REMOVE IMAGE</button>
          <span v-else></span>

          <button
            class="vsig-save-btn"
            @click="acceptVenueSignature"
            :disabled="applyingSign"
          >
            <span v-if="applyingSign"><i class="fa fa-spinner fa-spin"></i> Saving…</span>
            <span v-else>Save Signature</span>
          </button>
        </div>
      </el-dialog>

      <!-- ── Fill Field Modal ───────────────────────────────────────────── -->
      <el-dialog
        :title="activeFillField ? 'Fill ' + activeFillField.type.replace('_', ' ') + ' field' : 'Fill Field'"
        :visible.sync="showFillModal"
        width="420px"
        :append-to-body="true"
        custom-class="fill-field-modal"
        @opened="focusFillInput"
      >
        <div v-if="activeFillField" class="py-2">
          <input
            v-if="activeFillField.type === 'date'"
            v-model="fillFieldValue"
            type="date"
            class="form-control"
          />
          <input
            v-else
            ref="fillFieldInput"
            v-model="fillFieldValue"
            type="text"
            class="form-control"
            :placeholder="'Enter text…'"
            maxlength="200"
            @keydown.enter="acceptFillField"
          />
        </div>
        <div slot="footer">
          <button class="btn btn-outline-secondary btn-sm mr-2" @click="showFillModal = false">Cancel</button>
          <button class="btn btn-primary btn-sm" @click="acceptFillField" :disabled="!fillFieldValue.trim() || applyingFill">
            <span v-if="applyingFill"><i class="fa fa-spinner fa-spin"></i> Applying…</span>
            <span v-else>Apply</span>
          </button>
        </div>
      </el-dialog>

    </div><!-- /.cw-overlay -->
  </transition>
</template>

<script>
import PdfFieldEditor from '@/components/Contracts/PdfFieldEditor.vue';
import { Dialog } from 'element-ui';
import ContractInteractionService from '@/services/contract-interaction.service';

const FIELD_TYPES = {
  venue: [
    { type: 'signature', label: 'Signature Field', icon: 'fa-pencil' },
    { type: 'date',      label: 'Date Field',       icon: 'fa-calendar' },
    { type: 'text',      label: 'Text Field',       icon: 'fa-align-left' },
  ],
  venuecrew: [
    { type: 'signature', label: 'Signature Field', icon: 'fa-pencil' },
    { type: 'date',      label: 'Date Field',       icon: 'fa-calendar' },
    { type: 'text',      label: 'Text Field',       icon: 'fa-align-left' },
  ],
  client: [
    { type: 'signature', label: 'Signature Field', icon: 'fa-pencil' },
    { type: 'date',      label: 'Date Field',       icon: 'fa-calendar' },
    { type: 'text',      label: 'Text Field',       icon: 'fa-align-left' },
  ],
};

export default {
  name: 'ContractWizard',
  components: {
    PdfFieldEditor,
    [Dialog.name]: Dialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    leadEventId: {
      type: Number,
      required: true,
    },
    leadId: {
      type: Number,
      default: null,
    },
    venueId: {
      type: Number,
      default: null,
    },
    availableVenues: {
      type: Array,
      default: () => [],
    },
    existingContract: {
      type: Object,
      default: null,
    },
    initialPdfFile: {
      type: File,
      default: null,
    },
    clientName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      currentStep: 0,
      saving: false,
      VCfieldEditingDisabled: true,
      resolvedVenueId: null,
      vcSignRequired: false,
      venueSignRequired: false,
      contractId: null,
      pdfBytes: null,
      pdfFile: null,
      pdfFileName: null,
      venueFields: [],
      clientFields: [],
      vcFields: [],
      venueSignedPdfBlob: null,
      workingPdfBytes: null,
      showVenueSignModal: false,
      signTab: 'draw',
      sigPad: null,
      sigImageDataUrl: null,
      applyingSign: false,
      showFillModal: false,
      activeFillField: null,
      fillFieldValue: '',
      applyingFill: false,
      existingPdfBytes: null,
      notification: { show: false, type: 'info', message: '' },
      selectedFieldType: null,
      showAddFieldMenu: false,
      showInitialsMenu: false,
      stepToast: '',
      stepPulse: false,
      errorToast: '',
      signingParty: 'venuecrew',
      signDropdown: { visible: false, x: 0, y: 0 },
      activeSignField: null,
      typedSignature: '',
      typedSigFont: "'Dancing Script', 'Snell Roundhand', cursive",
      sigImageZoom: 1,
      sigImagePan: { x: 0, y: 0 },
      sigImageNaturalSize: null,
      sigContainerSize: null,
      sigPanDrag: null,
      sigPadIsEmpty: true,
      lastSignedInitialUrl: null,
      signatureFonts: [
        { family: "'Dancing Script', 'Snell Roundhand', cursive" },
        { family: "'Sacramento', 'Apple Chancery', cursive" },
        { family: "'Caveat', 'Bradley Hand', cursive" },
        { family: "'Pacifico', 'Brush Script MT', cursive" },
      ],
    };
  },
  computed: {
    effectiveVenueId() {
      return this.venueId || this.resolvedVenueId;
    },
    steps() {
      const arr = [];
      const initialStepTitle = this.VCfieldEditingDisabled ? 'Add Contract File (PDF only)' : 'Add Venue Fields';
      const initialStepSubtitle = this.VCfieldEditingDisabled
        ? 'Upload the contract PDF to continue.'
        : 'These are the fields the venue needs to fill out';
      if (this.vcSignRequired && !this.venueSignRequired) {
        arr.push({
          title: this.VCfieldEditingDisabled ? 'Add Contract File (PDF only)' : 'Add VenueCrew Fields',
          subtitle: this.VCfieldEditingDisabled
            ? 'Upload the contract PDF to continue.'
            : 'These are the fields VenueCrew needs to fill out',
          party: 'venuecrew',
        });
      } else {
        arr.push({ title: initialStepTitle, subtitle: initialStepSubtitle, party: 'venue' });
        if (this.vcSignRequired) {
          arr.push({ title: 'Add VenueCrew Fields', subtitle: 'These are the fields VenueCrew needs to fill out', party: 'venuecrew' });
        }
      }
      arr.push({ title: 'Add Client Fields', subtitle: 'These are the fields the client needs to fill out', party: 'client' });
      arr.push({ title: 'Sign Now', subtitle: 'Pre-sign the contract before sending', party: 'venuecrew', action: 'sign' });
      return arr;
    },
    currentParty() {
      const step = this.steps[this.currentStep];
      return step ? step.party : null;
    },
    isSignStep() {
      const step = this.steps[this.currentStep];
      return step ? step.action === 'sign' : false;
    },
    addFieldButtonLabel() {
      const labels = { venue: 'Add Fields for Venue', venuecrew: 'Add Fields for VenueCrew', client: 'Add Fields for Client' };
      return labels[this.currentParty] || 'Add Field';
    },
    venueName() {
      if (!this.effectiveVenueId) return '';
      const v = this.availableVenues.find(av => av.id === this.effectiveVenueId);
      return v ? v.name : '';
    },
    hasVenueSignatureFields() {
      return this.venueFields.some(f => f.type === 'signature' || f.type === 'initials');
    },
    hasVcSignatureFields() {
      return this.vcFields.some(f => f.type === 'signature' || f.type === 'initials');
    },
    allFields() {
      return [...this.venueFields, ...this.vcFields, ...this.clientFields];
    },
    isCurrentStepFieldEditingDisabled() {
      return this.VCfieldEditingDisabled && (this.currentParty === 'venue' || this.currentParty === 'venuecrew');
    },
    canProceed() {
      if (this.isCurrentStepFieldEditingDisabled && !this.isSignStep) {
        return this.pdfBytes !== null && this.effectiveVenueId !== null;
      }

      const party = this.currentParty;
      if (party === 'venue' || party === 'venuecrew') {
        return this.pdfBytes !== null && this.effectiveVenueId !== null;
      }
      if (party === 'client') {
        return this.clientFields.some(f => f.type === 'signature');
      }
      return true;
    },
    notificationIcon() {
      return {
        success: 'fa fa-check-circle',
        error:   'fa fa-exclamation-circle',
        warning: 'fa fa-exclamation-triangle',
        info:    'fa fa-info-circle',
      }[this.notification.type] || 'fa fa-info-circle';
    },
    signModalTitle() {
      return {
        draw:   'Draw Signature',
        type:   'Type Signature',
        upload: 'Upload Signature',
      }[this.signTab] || 'Signature';
    },
    signModalSubtitle() {
      return {
        draw:   'Use your mouse or finger (on mobile) to draw your signature below.',
        type:   'Type your signature and select font below.',
        upload: 'Accepts images in jpg, png and gif format.',
      }[this.signTab] || '';
    },
    sigZoomPct() {
      // Map zoom 1..4 to 0..100 percent for slider track fill
      const pct = ((this.sigImageZoom - 1) / 3) * 100;
      return Math.max(0, Math.min(100, pct));
    },
    sigPreviewStyle() {
      const field = this.activeSignField;
      // True aspect ratio = (width_pct * pageW) / (height_pct * pageH), since width_pct
      // and height_pct are fractions of different page dimensions.
      let ratio = 4;
      if (field && field.height_pct) {
        const editor = this.$refs.signEditor;
        const pages = editor && editor.renderedPages;
        const page = pages && pages[(field.page || 1) - 1];
        if (page && page.width && page.height) {
          ratio = (field.width_pct * page.width) / (field.height_pct * page.height);
        } else {
          ratio = field.width_pct / field.height_pct;
        }
      }
      const maxW = 440;
      const maxH = 220;
      let w = maxW;
      let h = w / ratio;
      if (h > maxH) { h = maxH; w = h * ratio; }
      return { width: Math.round(w) + 'px', height: Math.round(h) + 'px' };
    },
    sigBaseScale() {
      // Transform multiplier that brings the CSS-contained image up to cover the container.
      // Uses actual DOM container dimensions to stay in sync with canvas save.
      if (!this.sigImageNaturalSize || !this.sigContainerSize) return 1;
      const { w: cW, h: cH } = this.sigContainerSize;
      const { w, h } = this.sigImageNaturalSize;
      const coverScale = Math.max(cW / w, cH / h);
      const containScale = Math.min(cW / w, cH / h);
      return coverScale / containScale;
    },
  },
  watch: {
    visible(v) {
      if (v) this.initialize();
    },
    steps(newSteps, oldSteps) {
      // Reset fields when the party at step 0 changes due to checkbox toggles
      if (this.currentStep === 0 && newSteps[0] && oldSteps[0] && newSteps[0].party !== oldSteps[0].party) {
        if (oldSteps[0].party === 'venue') this.venueFields = [];
        if (oldSteps[0].party === 'venuecrew') this.vcFields = [];
      }
    },
    currentStep() {
      // Reset field type selection when changing steps
      this.selectedFieldType = null;
      this.showAddFieldMenu = false;
      // Auto-fill date fields with today's date when entering sign step
      if (this.isSignStep) {
        this.$nextTick(() => {
          const editor = this.$refs.signEditor;
          if (editor && editor.fields) {
            const now = new Date();
            const yyyy = now.getFullYear();
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const dd = String(now.getDate()).padStart(2, '0');
            const today = `${yyyy}-${mm}-${dd}`;
            editor.fields.forEach(f => {
              if (f.type === 'date' && f.executing_party === 'venuecrew') {
                this.$set(f, '_filledValue', today);
                ContractInteractionService.recordInteraction(this.leadEventId, this.effectiveVenueId, {
                  document_id: this.contractId,
                  field_id: f.id,
                  field_type: f.type,
                  interaction_type: 'value_updated',
                  field_value: today,
                  custom_label: f.custom_label || null,
                  lead_id: this.leadId,
                });
              }
            });
          }
        });
      }
    },
    venueSignRequired(enabled) {
      // Close field menus when the venue sign checkbox is unchecked on step 0
      if (!enabled && this.currentStep === 0 && this.currentParty === 'venue') {
        this.showAddFieldMenu = false;
        this.showInitialsMenu = false;
        this.selectedFieldType = null;
      }
    },
    sigImageZoom() { this.drawSigPreview(); },
    sigImagePan: { deep: true, handler() { this.drawSigPreview(); } },
    sigImageDataUrl(v) {
      if (v) this.$nextTick(() => this.drawSigPreview());
    },
    activeSignField() {
      this.$nextTick(() => {
        this.drawSigPreview();
        if (this.signTab === 'draw' && this.showVenueSignModal) this.initSignaturePad();
      });
    },
  },
  beforeDestroy() {
    this._teardownInitialScrollListener();
  },
  methods: {
    async initialize() {
      this.currentStep = 0;
      this.saving = false;
      this.resolvedVenueId = this.venueId;
      this.venueFields = [];
      this.clientFields = [];
      this.vcFields = [];
      this.vcSignRequired = false;
      this.venueSignRequired = false;
      this.venueSignedPdfBlob = null;
      this.pdfBytes = null;
      this.pdfFile = null;
      this.pdfFileName = null;
      this.existingPdfBytes = null;
      this.notification.show = false;
      this.selectedFieldType = null;
      this.showAddFieldMenu = false;
      this.lastSignedInitialUrl = null;
      ContractInteractionService.reset();

      if (this.existingContract) {
        this.contractId = this.existingContract.id;
        if (['sent_to_client', 'signed'].includes(this.existingContract.status)) {
          this.showNotification(
            'warning',
            'This contract has already been sent. Saving changes will create a new version.'
          );
        }
        const rawFields = this.existingContract.signature_fields || [];
        // Strip duplicates of unique-type fields (legacy data may contain them).
        // Keep only the first occurrence per (party, type) combination.
        const UNIQUE_TYPES = new Set(['signature', 'date']);
        const seen = new Set();
        const fields = rawFields.filter(f => {
          if (!UNIQUE_TYPES.has(f.type)) return true;
          const key = `${f.executing_party}|${f.type}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        this.venueFields = fields.filter(f => f.executing_party === 'venue');
        this.clientFields = fields.filter(f => f.executing_party === 'client');
        this.vcFields = fields.filter(f => f.executing_party === 'venuecrew');
        this.vcSignRequired = this.vcFields.length > 0;
        this.venueSignRequired = this.venueFields.some(f => f.type === 'signature' || f.type === 'initials');
        const attachment = (this.existingContract.attachments || []).filter(a => a.type === 'initial_contract').pop();
        if (attachment) {
          // Use server-provided document_hash to avoid mismatch with portal validation
          if (attachment.document_hash) {
            ContractInteractionService.setDocumentHash(attachment.document_hash);
          }
          await this.fetchExistingPdf(attachment.id, !attachment.document_hash);
        }
        // Push pre-existing own fields into each editor so they are visible and
        // the uniqueness guard can detect them. Must run after the next tick so
        // that $refs are fully initialised and any existingFields watchers have fired.
        await this.$nextTick();
        if (this.$refs.venueEditor && this.venueFields.length > 0) {
          this.$refs.venueEditor.preloadOwnFields(this.venueFields);
        }
        if (this.$refs.vcEditor && this.vcFields.length > 0) {
          this.$refs.vcEditor.preloadOwnFields(this.vcFields);
        }
        if (this.$refs.clientEditor && this.clientFields.length > 0) {
          this.$refs.clientEditor.preloadOwnFields(this.clientFields);
        }
      }
    },

    async fetchExistingPdf(attachmentId, computeHash = false) {
      try {
        const res = await this.$store.dispatch('contracts/getDownloadUrl', {
          leadEventId: this.leadEventId,
          attachmentId,
        });
        const url = (res && (res.download_url || res.url)) || res;
        const response = await fetch(url);
        const buffer = await response.arrayBuffer();
        this.existingPdfBytes = new Uint8Array(buffer);
        this.pdfBytes = this.existingPdfBytes;
        this.pdfFileName = 'contract.pdf';
        // Only compute hash locally if server didn't provide one
        if (computeHash) {
          ContractInteractionService.setDocumentBytes(buffer);
        }
      } catch (e) {
        console.warn('Could not load existing PDF:', e);
      }
    },

    onPdfLoaded({ file, bytes }) {
      this.pdfFile = file;
      this.pdfBytes = bytes;
      this.pdfFileName = file ? file.name : 'contract.pdf';
      // Only compute hash locally for new uploads (no server hash yet)
      if (!ContractInteractionService.getDocumentHashSync()) {
        ContractInteractionService.setDocumentBytes(bytes);
      }
    },

    onVenueFieldsUpdated(fields) { this.venueFields = fields; },
    onClientFieldsUpdated(fields) { this.clientFields = fields; },
    onVcFieldsUpdated(fields) { this.vcFields = fields; },

    // ── Add Field ────────────────────────────────────────────────────
    toggleAddFieldMenu() {
      if (this.showAddFieldMenu) {
        this.showAddFieldMenu = false;
        this.selectedFieldType = null;
      } else {
        // Always open fresh — clear any active placement mode and field selection first
        this.selectedFieldType = null;
        const refMap = {
          venue: this.$refs.venueEditor,
          venuecrew: this.$refs.vcEditor,
          client: this.$refs.clientEditor,
        };
        const editor = refMap[this.currentParty];
        if (editor) editor.clearSelection();
        this.showAddFieldMenu = true;
      }
    },

    closeAddFieldMenu() {
      this.showAddFieldMenu = false;
      this.showInitialsMenu = false;
      this.selectedFieldType = null;
    },

    isFieldTypeUsed(type) {
      const UNIQUE_TYPES = new Set();
      if (!UNIQUE_TYPES.has(type)) return false;
      const fields = this.currentParty === 'venue'
        ? this.venueFields
        : this.currentParty === 'venuecrew' ? this.vcFields
        : this.clientFields;
      return fields.some(f => f.type === type);
    },

    toggleInitialsMenu() {
      this.showInitialsMenu = !this.showInitialsMenu;
    },

    closeInitialsMenu() {
      this.showInitialsMenu = false;
    },

    placeInitialsOnAllPages(position) {
      this.showInitialsMenu = false;
      this.showAddFieldMenu = false;
      const refMap = {
        venue: this.$refs.venueEditor,
        venuecrew: this.$refs.vcEditor,
        client: this.$refs.clientEditor,
      };
      const editor = refMap[this.currentParty];
      if (editor) editor.placeInitialsOnAllPages(position);
    },

    selectFieldType(type) {
      if (this.isFieldTypeUsed(type)) return;
      this.selectedFieldType = type;
      this.showAddFieldMenu = false;
    },

    onFieldPlaced() {
      // One field per "Add Field" click — exit placement mode after placing
      this.selectedFieldType = null;
      this.showAddFieldMenu = false;
    },

    onDuplicateField(type) {
      const label = this.fieldTypeLabel(type);
      this.showNotification('error', `A "${label}" field already exists in this step. Only one is allowed.`);
    },

    onDuplicateLabel(label) {
      this.showNotification('error', `A text field labelled "${label}" already exists. Use a different label.`);
    },


    fieldTypeLabel(type) {
      const all = [...FIELD_TYPES.venue, ...FIELD_TYPES.venuecrew, ...FIELD_TYPES.client];
      const ft = all.find(f => f.type === type);
      return ft ? ft.label : type;
    },

    fieldTypeIcon(type) {
      const icons = {
        signature: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 14.5C4 11 5.5 9.5 7 9.5c1 0 1.5 1.5 2.5 1.5s2-2.5 3.5-4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><line x1="2" y1="17" x2="11" y2="17" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" opacity="0.5"/><path d="M14.5 4.5l2 2-3.5 6.5-2-.8 3.5-7.7z" fill="currentColor"/></svg>`,
        initials:  `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 15l3-10 3 10M4.5 11h3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M11 5v10h3a2.5 2.5 0 000-5h-3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M11 5h3a2.5 2.5 0 010 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
        date:      `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2.5" y="4.5" width="15" height="13" rx="2" stroke="currentColor" stroke-width="1.6"/><path d="M2.5 9h15" stroke="currentColor" stroke-width="1.4"/><path d="M6.5 3v3M13.5 3v3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><rect x="5.5" y="11.5" width="2" height="2" rx="0.5" fill="currentColor"/><rect x="9" y="11.5" width="2" height="2" rx="0.5" fill="currentColor"/><rect x="12.5" y="11.5" width="2" height="2" rx="0.5" fill="currentColor"/></svg>`,
        text:      `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 5h12M10 5v10M7 15h6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      };
      return icons[type] || '';
    },

    currentFieldTypes() {
      return FIELD_TYPES[this.currentParty] || FIELD_TYPES.venue;
    },

    panelFieldTypes() {
      // The 3 core options shown in the type-switcher panel
      return [
        { type: 'signature', label: 'Signature Field' },
        { type: 'date',      label: 'Date Field' },
        { type: 'text',      label: 'Text Field' },
      ];
    },

    // ── Navigation ────────────────────────────────────────────────────
    handleBack() {
      if (this.currentStep === 0) {
        // Step 1 back = close (parent shows upload modal again via replace-contract)
        this.$emit('replace-contract');
      } else {
        this.currentStep--;
      }
    },

    async goNext() {
      if (!this.canProceed) return;

      // Validate required fields before proceeding
      const validationError = this.isCurrentStepFieldEditingDisabled ? null : this.validateCurrentStepFields();
      if (validationError) {
        this.errorToast = validationError;
        setTimeout(() => { this.errorToast = ''; }, 5000);
        return;
      }

      const partyLabel = this.isCurrentStepFieldEditingDisabled ? null : {
        venue: 'Venue fields saved successfully',
        venuecrew: 'VenueCrew fields saved successfully',
        client: 'Client fields saved successfully',
      };
      const prevParty = this.currentParty;
      if (this.currentParty === 'client') {
        await this.saveAllFieldsAndProceed();
      } else {
        this.currentStep++;
      }
      // Show toast and pulse animation
      if (partyLabel) {
        this.stepToast = partyLabel[prevParty] || 'Fields saved successfully';
        this.stepPulse = true;
        setTimeout(() => { this.stepToast = ''; }, 3000);
        setTimeout(() => { this.stepPulse = false; }, 1000);
      }
    },

    validateCurrentStepFields() {
      const party = this.currentParty;
      const fields = party === 'venue' ? this.venueFields
        : party === 'venuecrew' ? this.vcFields
        : this.clientFields;

      const hasSignature = fields.some(f => f.type === 'signature');
      const hasDate = fields.some(f => f.type === 'date');

      // Get total page count from the relevant editor
      const refMap = { venue: this.$refs.venueEditor, venuecrew: this.$refs.vcEditor, client: this.$refs.clientEditor };
      const editor = refMap[party];
      const totalPages = editor && editor.renderedPages ? editor.renderedPages.length : 1;

      const initialsFields = fields.filter(f => f.type === 'initials');
      const pagesWithInitials = new Set(initialsFields.map(f => f.page));
      const missingInitialsPages = [];
      for (let p = 1; p <= totalPages; p++) {
        if (!pagesWithInitials.has(p)) missingInitialsPages.push(p);
      }

      // Check text fields have custom labels
      const textFieldsMissingLabel = fields.filter(f => f.type === 'text' && !f.custom_label);
      if (textFieldsMissingLabel.length > 0) {
        return 'All text fields must have a custom label. Double-click to edit.';
      }

      const missing = [];
      if (! hasSignature) missing.push('Signature field');
      if (! hasDate) missing.push('Date field');
      if (missingInitialsPages.length > 0) {
        if (missingInitialsPages.length === totalPages) {
          missing.push('Initials on all pages');
        } else {
          missing.push(`Initials on page${missingInitialsPages.length > 1 ? 's' : ''} ${missingInitialsPages.join(', ')}`);
        }
      }

      if (missing.length > 0) {
        return `Required fields missing: ${missing.join(', ')}.`;
      }
      return null;
    },

    validateAllPartiesFields() {
      // Validate each party that requires signing has the necessary fields
      const parties = [];
      if (!this.VCfieldEditingDisabled && this.venueSignRequired) parties.push({ label: 'Venue', fields: this.venueFields });
      if (!this.VCfieldEditingDisabled && this.vcSignRequired) parties.push({ label: 'VenueCrew', fields: this.vcFields });
      parties.push({ label: 'Client', fields: this.clientFields });

      // Get total pages from sign editor (has all fields loaded)
      const editor = this.$refs.signEditor || this.$refs.venueEditor || this.$refs.clientEditor;
      const totalPages = editor && editor.renderedPages ? editor.renderedPages.length : 1;

      const errors = [];
      for (const { label, fields } of parties) {
        const missing = [];
        if (! fields.some(f => f.type === 'signature')) missing.push('Signature');
        if (! fields.some(f => f.type === 'date')) missing.push('Date');
        const initialsFields = fields.filter(f => f.type === 'initials');
        const pagesWithInitials = new Set(initialsFields.map(f => f.page));
        const missingPages = [];
        for (let p = 1; p <= totalPages; p++) {
          if (!pagesWithInitials.has(p)) missingPages.push(p);
        }
        if (missingPages.length > 0) missing.push('Initials on all pages');
        if (missing.length > 0) {
          errors.push(`${label}: ${missing.join(', ')}`);
        }
      }

      if (errors.length > 0) {
        return `Cannot send — required fields missing. ${errors.join('. ')}.`;
      }
      return null;
    },

    async saveAllFieldsAndProceed() {
      this.saving = true;
      try {
        const allFields = [...this.venueFields, ...this.vcFields, ...this.clientFields];

        // Validate text fields have custom_label
        const textFieldsMissingLabel = allFields.filter(f => f.type === 'text' && !f.custom_label);
        if (textFieldsMissingLabel.length > 0) {
          this.errorToast = 'All text fields must have a custom label.';
          setTimeout(() => { this.errorToast = ''; }, 5000);
          this.saving = false;
          return;
        }

        const payload = {
          venue_id: this.effectiveVenueId,
          signature_fields: allFields,
          vc_sign_required: this.vcSignRequired,
          venue_sign_required: this.venueSignRequired,
        };

        const res = await this.$store.dispatch('contracts/createOrUpdateContract', {
          leadEventId: this.leadEventId,
          payload,
        });
        const contract = res && res.data && res.data.contract;
        this.contractId = contract && contract.id;
        if (!this.contractId) {
          console.error('Contract ID missing from response:', res);
          throw new Error('No contract ID returned from server');
        }

        const firstParty = this.steps[0].party;
        const firstRef = firstParty === 'venuecrew' ? this.$refs.vcEditor : this.$refs.venueEditor;
        const originalPdfFile = firstRef && firstRef.getPdfFile();
        if (originalPdfFile) {
          const uploadRes = await this.$store.dispatch('contracts/uploadPdf', {
            leadEventId: this.leadEventId,
            contractId: this.contractId,
            file: originalPdfFile,
          });
          // Use server-computed hash if returned in upload response
          const uploadData = uploadRes && uploadRes.data;
          const serverHash = uploadData && (uploadData.document_hash
            || (uploadData.attachment && uploadData.attachment.document_hash));
          if (serverHash) {
            ContractInteractionService.setDocumentHash(serverHash);
          }
        }
        this.currentStep++;
      } catch (e) {
        console.error('saveAllFieldsAndProceed error:', e);
        this.showNotification('error', 'Failed to save fields. Please try again.');
        this.errorToast = 'Failed to save fields. Please try again.';
        setTimeout(() => { this.errorToast = ''; }, 5000);
      } finally {
        this.saving = false;
      }
    },

    async completeSend() {
      // Validate all parties have required fields before sending
      const validationError = this.validateAllPartiesFields();
      if (validationError) {
        this.errorToast = validationError;
        setTimeout(() => { this.errorToast = ''; }, 6000);
        return;
      }

      // Validate all VC fields are signed/filled before sending to client
      if (this.vcSignRequired) {
        const signEditorFields = this.$refs.signEditor ? this.$refs.signEditor.fields : [];
        const vcFields = signEditorFields.filter(f => f.executing_party === 'venuecrew');
        const unsignedFields = vcFields.filter(f => {
          if (f.type === 'signature' || f.type === 'initials') {
            return ! f._signedDataUrl;
          }
          return ! f._filledValue || ! f._filledValue.trim();
        });
        if (unsignedFields.length > 0) {
          const types = [...new Set(unsignedFields.map(f => f.type.replace('_', ' ')))];
          this.errorToast = `Please sign/fill all VenueCrew fields before sending. Missing: ${types.join(', ')}`;
          setTimeout(() => { this.errorToast = ''; }, 6000);
          return;
        }
      }

      this.saving = true;
      try {
        // 1. Submit venuecrew signed fields to the API for server-side PDF embedding
        if (this.vcSignRequired) {
          const signEditorFields = this.$refs.signEditor ? this.$refs.signEditor.fields : [];
          const vcFields = signEditorFields.filter(f => f.executing_party === 'venuecrew');

          // Refresh date fields to current date right before signing to match server validation
          const now = new Date();
          const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          vcFields.forEach(f => {
            if (f.type === 'date') {
              this.$set(f, '_filledValue', today);
            }
          });

          const fields = vcFields.map(f => ({
            field_id: f.id,
            content: (f.type === 'signature' || f.type === 'initials')
              ? f._signedDataUrl
              : (f._filledValue || ''),
          }));
          await this.$store.dispatch('contracts/signContract', {
            leadEventId: this.leadEventId,
            contractId: this.contractId,
            payload: { signing_party: 'venuecrew', signed_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, fields },
          });
        }
        // 2. Send to client
        await this.$store.dispatch('contracts/sendToClient', {
          leadEventId: this.leadEventId,
          contractId: this.contractId,
        });
        this.$emit('contract-sent', this.contractId);
        this.$emit('update:visible', false);
      } catch (e) {
        this.errorToast = 'Failed to send contract. Please try again.';
        setTimeout(() => { this.errorToast = ''; }, 5000);
      } finally {
        this.saving = false;
      }
    },
    // ── Sign field (step 3) ───────────────────────────────────────────────────
    onSignField({ field, event }) {
      this._awaitingInitialScroll = false;
      this._teardownInitialScrollListener(); // clear any stale listener from a previous auto-apply
      this.activeSignField = field;
      this.signingParty = field.executing_party;

      // Record focused_on_field interaction
      ContractInteractionService.recordInteraction(this.leadEventId, this.effectiveVenueId, {
        document_id: this.contractId,
        field_id: field.id,
        field_type: field.type,
        interaction_type: 'focused_on_field',
        field_value: null,
        custom_label: field.custom_label || null,
        lead_id: this.leadId,
      });

      // For unsigned initials when a previous initial exists, auto-apply immediately
      // and hide the dropdown. If user clicks again on the already-signed field,
      // the dropdown will show to let them change it.
      if (field.type === 'initials' && !field._signedDataUrl && this.lastSignedInitialUrl) {
        this.$set(field, '_signedDataUrl', this.lastSignedInitialUrl);
        this._awaitingInitialScroll = true;
        this._setupInitialScrollListener();
        // Don't show the dropdown — auto-applied silently
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const dropdownWidth = 260;
      const dropdownHeight = 280; // approximate height of 3 options with icon circles + padding
      const gap = 14;
      const arrowCenter = 38; // top: 28px + 10px (half of 20px arrow)

      let x = rect.right + gap;
      let flipped = false;
      // Flip to left side if not enough room on the right
      if (x + dropdownWidth > window.innerWidth - 8) {
        x = rect.left - dropdownWidth - gap;
        flipped = true;
      }
      let y = rect.top + rect.height / 2 - arrowCenter;
      // Clamp to viewport so the dropdown doesn't get cropped at the bottom
      const maxY = window.innerHeight - dropdownHeight - 8;
      if (y > maxY) y = maxY;
      if (y < 8) y = 8;
      this.signDropdown = { visible: true, x, y, flipped };
    },

    openSignModal(tab) {
      this.signTab = tab;
      this.showVenueSignModal = true;
      this.signDropdown = { visible: false, x: 0, y: 0 };
      // User chose to re-draw — cancel the auto-scroll listener.
      this._awaitingInitialScroll = false;
      this._teardownInitialScrollListener();
    },

    closeSignDropdown() {
      this.signDropdown = { visible: false, x: 0, y: 0 };
      // NOTE: do NOT teardown the scroll listener here — the v-click-outside
      // directive fires on the same click that opened the dropdown, which would
      // remove the listener before the user ever gets to scroll.
    },

    // ── Initials scroll-to-next helpers ───────────────────────────────────────
    scrollToNextUnsignedInitial() {
      this.$nextTick(() => {
        const signEditor = this.$refs.signEditor;
        if (! signEditor) return;
        const scrollContainer = this.$el && this.$el.querySelector('.cw-content');
        if (! scrollContainer) return;

        // Use in-memory field data (authoritative) to find the next unsigned initials
        // for this signing party, ordered by page then vertical position.
        const fields = (signEditor.fields || [])
          .filter(f => f.type === 'initials' && f.executing_party === this.signingParty && ! f._signedDataUrl)
          .sort((a, b) => (a.page !== b.page ? a.page - b.page : a.y_pct - b.y_pct));

        if (! fields.length) return;

        // Locate the DOM element by the data-field-id attribute we set on every field div.
        const targetEl = signEditor.$el.querySelector(`[data-field-id="${fields[0].id}"]`);
        if (! targetEl) return;

        const containerRect = scrollContainer.getBoundingClientRect();
        const fieldRect = targetEl.getBoundingClientRect();
        const targetOffset = fieldRect.top - containerRect.top + scrollContainer.scrollTop
          - scrollContainer.clientHeight / 2 + fieldRect.height / 2;

        // Custom smooth scroll using requestAnimationFrame — immune to browser
        // gesture cancellation (unlike native scrollTo smooth).
        this._animateScroll(scrollContainer, targetOffset, 400);

        targetEl.classList.add('cw-field-pulse');
        setTimeout(() => targetEl.classList.remove('cw-field-pulse'), 1400);
      });
    },

    _animateScroll(container, targetTop, duration) {
      // Cancel any in-progress animation.
      if (this._scrollAnimFrame) cancelAnimationFrame(this._scrollAnimFrame);

      const startTop = container.scrollTop;
      const distance = targetTop - startTop;
      if (Math.abs(distance) < 1) return;

      const startTime = performance.now();
      const ease = t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // easeInOutQuad

      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        container.scrollTop = startTop + distance * ease(progress);
        if (progress < 1) {
          this._scrollAnimFrame = requestAnimationFrame(step);
        } else {
          this._scrollAnimFrame = null;
        }
      };
      this._scrollAnimFrame = requestAnimationFrame(step);
    },

    _setupInitialScrollListener() {
      const scrollContainer = this.$el && this.$el.querySelector('.cw-content');
      if (! scrollContainer) return;
      this._teardownInitialScrollListener();

      this._initialScrollHandler = () => {
        if (!this._awaitingInitialScroll) return;

        // Immediately hide the dropdown and scroll to next field.
        this._awaitingInitialScroll = false;
        this._teardownInitialScrollListener();
        this.signDropdown = { visible: false, x: 0, y: 0 };
        this.scrollToNextUnsignedInitial();
      };

      scrollContainer.addEventListener('wheel', this._initialScrollHandler, { passive: true });
      scrollContainer.addEventListener('touchstart', this._initialScrollHandler, { passive: true });
    },

    _teardownInitialScrollListener() {
      const el = this.$el;
      const scrollContainer = el && el.querySelector && el.querySelector('.cw-content');
      if (scrollContainer && this._initialScrollHandler) {
        scrollContainer.removeEventListener('wheel', this._initialScrollHandler);
        scrollContainer.removeEventListener('touchstart', this._initialScrollHandler);
      }
      this._initialScrollHandler = null;
    },

    onFillField({ field, event }) {
      this.activeFillField = field;

      // Record focused_on_field interaction
      ContractInteractionService.recordInteraction(this.leadEventId, this.effectiveVenueId, {
        document_id: this.contractId,
        field_id: field.id,
        field_type: field.type,
        interaction_type: 'focused_on_field',
        field_value: null,
        custom_label: field.custom_label || null,
        lead_id: this.leadId,
      });

      if (field._filledValue) {
        this.fillFieldValue = field._filledValue;
      } else if (field.type === 'date') {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        this.fillFieldValue = `${yyyy}-${mm}-${dd}`;
      } else {
        this.fillFieldValue = '';
      }
      this.showFillModal = true;
    },

    acceptFillField() {
      const value = this.fillFieldValue.trim();
      if (!value || !this.activeFillField) return;
      this.$set(this.activeFillField, '_filledValue', value);
      this.showFillModal = false;
      this.showNotification('success', 'Field value applied.');

      // Record value_updated interaction
      ContractInteractionService.recordInteraction(this.leadEventId, this.effectiveVenueId, {
        document_id: this.contractId,
        field_id: this.activeFillField.id,
        field_type: this.activeFillField.type,
        interaction_type: 'value_updated',
        field_value: value,
        custom_label: this.activeFillField.custom_label || null,
        lead_id: this.leadId,
      });
    },

    focusFillInput() {
      this.$nextTick(() => {
        const input = this.$refs.fillFieldInput;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },

    // ── Venue Signature Modal ─────────────────────────────────────────
    openVenueSignModal() {
      this.signingParty = 'venue';
      this.sigImageDataUrl = null;
      this.sigImageZoom = 1;
      this.sigImagePan = { x: 0, y: 0 };
      this.sigImageNaturalSize = null;
      this.sigContainerSize = null;
      this.scrollToFirstSignableField();
    },

    openVcSignModal() {
      this.signingParty = 'venuecrew';
      this.sigImageDataUrl = null;
      this.sigImageZoom = 1;
      this.sigImagePan = { x: 0, y: 0 };
      this.sigImageNaturalSize = null;
      this.sigContainerSize = null;
      this.signTab = 'draw';
      this.showVenueSignModal = true;
      this.scrollToFirstSignableField();
    },

    scrollToVcSignField() {
      this.signingParty = 'venuecrew';
      this.$nextTick(() => {
        const signEditor = this.$refs.signEditor;
        if (!signEditor) return;
        const fieldEl = signEditor.$el.querySelector('.pdf-field--party-venuecrew.pdf-field--sign-mode.pdf-field--signature');
        if (!fieldEl) return;
        const scrollContainer = this.$el.querySelector('.cw-content');
        if (!scrollContainer) return;
        const containerRect = scrollContainer.getBoundingClientRect();
        const fieldRect = fieldEl.getBoundingClientRect();
        const offset = fieldRect.top - containerRect.top + scrollContainer.scrollTop - (scrollContainer.clientHeight / 2) + (fieldRect.height / 2);
        scrollContainer.scrollTo({ top: offset, behavior: 'smooth' });
        // Briefly pulse the field to draw the user's attention
        fieldEl.classList.add('cw-field-pulse');
        setTimeout(() => fieldEl.classList.remove('cw-field-pulse'), 1400);
      });
    },

    scrollToFirstSignableField() {
      this.$nextTick(() => {
        const signEditor = this.$refs.signEditor;
        if (! signEditor) return;
        // Find the first signable field element for the current signing party
        const partyClass = this.signingParty === 'venuecrew' ? '.pdf-field--party-venuecrew' : '.pdf-field--party-venue';
        const fieldEl = signEditor.$el.querySelector(partyClass + '.pdf-field--sign-mode');
        if (! fieldEl) return;
        // Scroll the cw-content container so the field is centred vertically
        const scrollContainer = this.$el.querySelector('.cw-content');
        if (! scrollContainer) return;
        const containerRect = scrollContainer.getBoundingClientRect();
        const fieldRect = fieldEl.getBoundingClientRect();
        const offset = fieldRect.top - containerRect.top + scrollContainer.scrollTop - (scrollContainer.clientHeight / 2) + (fieldRect.height / 2);
        scrollContainer.scrollTo({ top: offset, behavior: 'smooth' });
      });
    },

    onSignModalOpen() {
      this.ensureSignatureFontsLoaded();
      if (this.signTab === 'draw') this.initSignaturePad();
      if (this.signTab === 'upload' && this.sigImageDataUrl) {
        this.$nextTick(() => {
          const el = document.querySelector('.vsig-upload-preview');
          if (el) this.sigContainerSize = { w: el.clientWidth, h: el.clientHeight };
          this.drawSigPreview();
        });
      }
    },

    ensureSignatureFontsLoaded() {
      if (document.querySelector('link[data-vsig-fonts]')) return;
      const link = document.createElement('link');
      link.setAttribute('data-vsig-fonts', '1');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Caveat&family=Dancing+Script&family=Pacifico&family=Sacramento&display=swap';
      document.head.appendChild(link);
    },

    removeSigImage() {
      this.sigImageDataUrl = null;
      this.sigImageZoom = 1;
      this.sigImagePan = { x: 0, y: 0 };
      this.sigImageNaturalSize = null;
      this.sigContainerSize = null;
      this._sigPreviewImg = null;
    },

    onSigPanStart(e) {
      e.preventDefault();
      const startX = e.clientX - this.sigImagePan.x;
      const startY = e.clientY - this.sigImagePan.y;
      const onMove = (ev) => {
        this.sigImagePan = { x: ev.clientX - startX, y: ev.clientY - startY };
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        this.sigPanDrag = null;
      };
      this.sigPanDrag = true;
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },

    initSignaturePad() {
      this.$nextTick(async () => {
        const canvas = this.$refs.sigCanvas;
        if (!canvas) return;
        // Match the drawing buffer to the display size (DPR-aware) so signature_pad
        // coordinates aren't stretched and the saved image has the field's aspect ratio.
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const displayW = canvas.clientWidth || parseInt(this.sigPreviewStyle.width);
        const displayH = canvas.clientHeight || parseInt(this.sigPreviewStyle.height);
        canvas.width = displayW * ratio;
        canvas.height = displayH * ratio;
        canvas.getContext('2d').scale(ratio, ratio);
        const SignaturePad = await this.getSignaturePad();
        if (this.sigPad) this.sigPad.off();
        this.sigPadIsEmpty = true;
        this.sigPad = new SignaturePad(canvas, { backgroundColor: 'rgba(0,0,0,0)' });
        this.sigPad.addEventListener('beginStroke', () => { this.sigPadIsEmpty = false; });
        this.sigPad.addEventListener('endStroke', () => {
          this.sigPadIsEmpty = this.sigPad.isEmpty();
        });
      });
    },

    async getSignaturePad() {
      if (window.SignaturePad) return window.SignaturePad;
      await new Promise((resolve, reject) => {
        if (document.querySelector('script[data-sigpad]')) { resolve(); return; }
        const s = document.createElement('script');
        s.setAttribute('data-sigpad', '1');
        s.src = 'https://cdn.jsdelivr.net/npm/signature_pad@4.2.0/dist/signature_pad.umd.min.js';
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
      return window.SignaturePad;
    },

    clearSignaturePad() {
      if (this.sigPad) this.sigPad.clear();
      this.sigPadIsEmpty = true;
    },

    onSigImageUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          this.sigImageNaturalSize = { w: img.naturalWidth, h: img.naturalHeight };
          this._sigPreviewImg = img;
          this.sigImageDataUrl = ev.target.result;
          this.$nextTick(() => {
            const el = document.querySelector('.vsig-upload-preview');
            if (el) this.sigContainerSize = { w: el.clientWidth, h: el.clientHeight };
            this.drawSigPreview();
          });
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    },

    drawSigPreview() {
      const canvas = document.querySelector('.vsig-upload-canvas');
      if (!canvas || !this._sigPreviewImg || !this.sigImageNaturalSize) return;
      const previewEl = canvas.parentElement;
      const cW = previewEl.clientWidth;
      const cH = previewEl.clientHeight;
      if (canvas.width !== cW) canvas.width = cW;
      if (canvas.height !== cH) canvas.height = cH;
      const nat = this.sigImageNaturalSize;
      const coverScale = Math.max(cW / nat.w, cH / nat.h);
      const scaledW = nat.w * coverScale * this.sigImageZoom;
      const scaledH = nat.h * coverScale * this.sigImageZoom;
      const dx = (cW - scaledW) / 2 + this.sigImagePan.x;
      const dy = (cH - scaledH) / 2 + this.sigImagePan.y;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, cW, cH);
      ctx.drawImage(this._sigPreviewImg, dx, dy, scaledW, scaledH);
    },

    renderTypedSignature(text) {
      // Match the field's true on-page aspect ratio so it fits without letterboxing
      const field = this.activeSignField;
      let ratio = 4;
      if (field && field.height_pct) {
        const editor = this.$refs.signEditor;
        const pages = editor && editor.renderedPages;
        const page = pages && pages[(field.page || 1) - 1];
        ratio = (page && page.width && page.height)
          ? (field.width_pct * page.width) / (field.height_pct * page.height)
          : field.width_pct / field.height_pct;
      }
      const canvasW = 800;
      const canvasH = Math.round(canvasW / ratio);
      const canvas = document.createElement('canvas');
      canvas.width = canvasW;
      canvas.height = canvasH;
      const ctx = canvas.getContext('2d');
      // Padding inside the canvas so the text doesn't touch edges
      const padX = canvasW * 0.06;
      const padY = canvasH * 0.12;
      const maxW = canvasW - padX * 2;
      const maxH = canvasH - padY * 2;
      // Auto-size font: start from a height-based estimate and shrink if too wide
      let fontSize = maxH;
      ctx.font = fontSize + 'px ' + this.typedSigFont;
      let measured = ctx.measureText(text).width;
      if (measured > maxW) {
        fontSize = fontSize * (maxW / measured);
        ctx.font = fontSize + 'px ' + this.typedSigFont;
      }
      ctx.fillStyle = '#1a1a4e';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText(text, canvasW / 2, canvasH / 2);
      return canvas.toDataURL('image/png');
    },

    async acceptVenueSignature() {
      let dataUrl = null;
      if (this.signTab === 'draw') {
        if (!this.sigPad || this.sigPad.isEmpty()) {
          this.$message && this.$message.warning('Please draw your signature first.');
          return;
        }
        dataUrl = this.sigPad.toDataURL('image/png');
      } else if (this.signTab === 'type') {
        if (!this.typedSignature.trim()) {
          this.$message && this.$message.warning('Please type your name first.');
          return;
        }
        dataUrl = this.renderTypedSignature(this.typedSignature.trim());
      } else {
        if (! this.sigImageDataUrl) {
          this.$message && this.$message.warning('Please upload a signature image first.');
          return;
        }
        // The preview IS a canvas — what you see is what you get
        this.drawSigPreview();
        const canvas = document.querySelector('.vsig-upload-canvas');
        dataUrl = canvas ? canvas.toDataURL('image/png') : this.sigImageDataUrl;
      }

      this.applyingSign = true;
      try {
        // Update signEditor's internal field copies directly — they are the source of truth
        // for both the UI display and the PDF rebuild (onClearSign also targets these copies).
        const signEditorFields = this.$refs.signEditor ? this.$refs.signEditor.fields : [];
        const activeType = this.activeSignField ? this.activeSignField.type : 'signature';
        let signableFields;
        if (activeType === 'initials') {
          // Apply only to the specific field that was clicked; store for future auto-apply
          signableFields = signEditorFields.filter(f => f.id === this.activeSignField.id);
          this.lastSignedInitialUrl = dataUrl;
        } else {
          // Signature: update all signature-type fields for this party (never touches initials)
          signableFields = signEditorFields.filter(f =>
            f.executing_party === this.signingParty && f.type === activeType
          );
        }
        signableFields.forEach(f => { this.$set(f, '_signedDataUrl', dataUrl); });
        this.showVenueSignModal = false;
        const partyLabel = this.signingParty === 'venuecrew' ? 'VenueCrew' : 'Venue';
        this.showNotification('success', partyLabel + ' signature saved.');

        // Record value_updated interaction for each signed field
        signableFields.forEach(f => {
          ContractInteractionService.hashValue(dataUrl).then(hash => {
            ContractInteractionService.recordInteraction(this.leadEventId, this.effectiveVenueId, {
              document_id: this.contractId,
              field_id: f.id,
              field_type: f.type,
              interaction_type: 'value_updated',
              field_value: hash,
              custom_label: f.custom_label || null,
              lead_id: this.leadId,
            });
          });
        });
        // After manually signing an initials field, jump to the next unsigned one.
        if (activeType === 'initials') {
          this.scrollToNextUnsignedInitial();
        }
      } catch (e) {
        this.showNotification('error', 'Failed to save signature.');
      } finally {
        this.applyingSign = false;
      }
    },

    onClearSign(field) {
      if (field.type === 'initials') {
        const signEditorFields = this.$refs.signEditor ? this.$refs.signEditor.fields : [];
        const urlToRemove = field._signedDataUrl;
        if (urlToRemove) {
          // Remove all initials fields that share the same signed image (same group)
          signEditorFields.forEach(f => {
            if (f.type === 'initials' && f.executing_party === field.executing_party && f._signedDataUrl === urlToRemove) {
              this.$set(f, '_signedDataUrl', null);
            }
          });
          // Reset last-initial cache if no signed initials remain for this party
          const anyStillSigned = signEditorFields.some(
            f => f.type === 'initials' && f.executing_party === field.executing_party && f._signedDataUrl
          );
          if (!anyStillSigned) {
            this.lastSignedInitialUrl = null;
          }
        }
        return;
      }
      this.$set(field, '_signedDataUrl', null);
    },

    showNotification(type, message) {
      this.notification = { show: true, type, message };
    },
  },
};
</script>

<style scoped>
/* ── Overlay ──────────────────────────────────────────────────────── */
.cw-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  overflow: hidden;
}

/* Field pulse animation triggered from JS */
::v-deep .cw-field-pulse {
  animation: cw-pulse 0.35s ease-in-out 0s 4 alternate;
}
@keyframes cw-pulse {
  from { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.15); }
  to   { box-shadow: 0 0 0 8px rgba(2, 132, 199, 0.55); }
}

/* ── Transition ───────────────────────────────────────────────────── */
.cw-fade-enter-active,
.cw-fade-leave-active {
  transition: opacity 0.2s ease;
}
.cw-fade-enter,
.cw-fade-leave-to {
  opacity: 0;
}

/* ── Header ───────────────────────────────────────────────────────── */
.cw-header {
  flex-shrink: 0;
  text-align: center;
  padding: 28px 24px 12px;
  background: #f0f2f5;
  position: relative;
}

.cw-close {
  position: absolute;
  top: 16px;
  right: 20px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.cw-close:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #1a1a2e;
}

.cw-title {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 28px;
  font-weight: 400;
  color: #1a1a2e;
  margin: 0 0 6px;
  letter-spacing: -0.01em;
}

.cw-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* ── Notification ─────────────────────────────────────────────────── */
.cw-notification {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  font-size: 13px;
  margin: 0 24px 8px;
  border-radius: 6px;
}
.cw-notification--info    { background: #ecf5ff; color: #409eff; }
.cw-notification--success { background: #f0f9eb; color: #67c23a; }
.cw-notification--warning { background: #fdf6ec; color: #e6a23c; }
.cw-notification--error   { background: #fef0f0; color: #f56c6c; }
.cw-notification-close {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.6;
  line-height: 1;
  padding: 0;
}

/* ── Content ──────────────────────────────────────────────────────── */
.cw-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 0 8px;
}

/* ── Step 3 ───────────────────────────────────────────────────────── */
.cw-step3 {
  display: flex;
  justify-content: center;
  padding: 40px 24px;
}

.cw-step3-inner {
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.cw-send-summary {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.cw-send-summary-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #fef2f2;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.cw-send-summary-name {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
}

.cw-send-summary-meta {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

/* ── Footer ───────────────────────────────────────────────────────── */
.cw-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
}

.cw-footer-left {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
}

.cw-footer-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Back button */
.cw-back-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1.5px solid #d1d5db;
  background: #fff;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  flex-shrink: 0;
}
.cw-back-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}
.cw-back-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Step info */
.cw-step-info {
  line-height: 1.3;
}

.cw-step-label {
  font-size: 10px;
  font-weight: 700;
  color: #7c3aed;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  display: block;
}

/* Step toast */
.cw-step-toast {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 60px;
  background: #065f46;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  z-index: 100;
  pointer-events: none;
}
.cw-step-toast .fa {
  margin-right: 6px;
  color: #34d399;
}
.cw-step-toast--error {
  background: #991b1b;
  font-size: 15px;
  padding: 12px 20px;
  white-space: normal;
  max-width: 720px;
  min-width: 400px;
  line-height: 1.4;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(153, 27, 27, 0.35);
}
.cw-step-toast--error .fa {
  color: #fca5a5;
  font-size: 16px;
}
.cw-toast-fade-enter-active,
.cw-toast-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.cw-toast-fade-enter,
.cw-toast-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* Step info pulse animation */
.cw-step-info--pulse {
  animation: cw-step-pulse 1s ease;
}
@keyframes cw-step-pulse {
  0%   { transform: scale(1); }
  30%  { transform: scale(1.18); }
  60%  { transform: scale(1.18); }
  100% { transform: scale(1); }
}

.cw-step-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.cw-step-sub {
  font-size: 12px;
  color: #9ca3af;
}

/* Change Contract File button */
.cw-btn-replace {
  border: 1.5px solid #d1d5db;
  background: #fff;
  color: #111827;
  border-radius: 999px;
  padding: 12px 26px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.cw-btn-replace:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

/* Add Field button */
.cw-add-field-wrap {
  position: relative;
}

.cw-btn-add-field {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1.5px solid #111827;
  background: #111827;
  color: #fff;
  border-radius: 999px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  transition: background 0.15s, border-color 0.15s;
}
.cw-btn-add-field:hover:not(:disabled) {
  background: #000;
  border-color: #000;
}
.cw-btn-add-field--active {
  background: #000;
  border-color: #000;
  color: #fff;
}
.cw-btn-add-field--active:hover:not(:disabled) {
  background: #1f2937;
  border-color: #1f2937;
}

/* Add Initials button */
.cw-add-initials-wrap {
  position: relative;
}

.cw-btn-add-initials {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1.5px solid #4c1d95;
  background: #fff;
  color: #4c1d95;
  border-radius: 999px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.cw-btn-add-initials:hover:not(:disabled) {
  background: #f5f3ff;
}
.cw-btn-add-initials--active,
.cw-btn-add-initials--active:hover:not(:disabled) {
  background: #4c1d95;
  color: #fff;
}
.cw-btn-add-initials:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Initials position picker */
.cw-initials-menu {
  position: absolute;
  bottom: calc(100% + 10px);
  right: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.14);
  padding: 14px 16px 12px;
  z-index: 20;
  min-width: 200px;
}

.cw-field-menu-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0;
}

.cw-initials-submenu {
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
}

.cw-initials-menu-label {
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 10px;
}

.cw-initials-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.cw-initials-opt {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 8px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  transition: border-color 0.15s, background 0.15s;
}
.cw-initials-opt:hover {
  border-color: #4c1d95;
  background: #f5f3ff;
  color: #4c1d95;
}

.cw-initials-icon {
  display: block;
  line-height: 0;
}

/* Add Field menu */
.cw-field-menu {
  position: absolute;
  bottom: calc(100% + 10px);
  right: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  min-width: 210px;
  z-index: 10;
  overflow: hidden;
}

.cw-field-menu-item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  font-size: 14px;
  color: #1a1a2e;
  background: none;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.12s;
  font-weight: 500;
  position: relative;
}
.cw-field-menu-item:last-child {
  border-bottom: none;
}
.cw-field-menu-item:hover {
  background: #f8f5ff;
}
.cw-field-menu-item--active {
  background: #ede9fe;
  color: #5b21b6;
  font-weight: 600;
}
.cw-field-menu-item--used {
  opacity: 0.38;
  cursor: not-allowed;
}
.cw-field-menu-item--used:hover {
  background: none;
}
.cw-field-menu-sep { display: none; }

.cw-field-menu-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #ede9fe;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6d28d9;
  flex-shrink: 0;
}
.cw-field-menu-item--active .cw-field-menu-icon {
  background: #c4b5fd;
  color: #4c1d95;
}

/* Continue / Send button */
.cw-btn-continue {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #8866C5;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 12px 26px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  transition: background 0.15s, opacity 0.15s, box-shadow 0.15s;
}
.cw-btn-continue:hover:not(:disabled) {
  background: #764fb9;
}
.cw-btn-continue:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Signature canvas */
.sig-canvas {
  width: 100%;
  max-width: 440px;
  height: 180px;
  cursor: crosshair;
  background: #fff;
  touch-action: none;
}

/* Sign dropdown — mirrors pdf-type-switcher exactly */
.cw-sign-dropdown {
  position: fixed;
  z-index: 4000;
  background: #fff;
  border-radius: 0;
  box-shadow: 0 14px 40px rgba(20, 20, 40, 0.18), 0 4px 14px rgba(20, 20, 40, 0.10);
  min-width: 240px;
  padding: 8px;
  user-select: none;
  display: flex;
  flex-direction: column;
  gap: 0;
}
/* Arrow pointing left toward the field — same as pdf-type-switcher */
.cw-sign-dropdown::before,
.cw-sign-dropdown::after {
  content: '';
  position: absolute;
  top: 28px;
  width: 0;
  height: 0;
  border-style: solid;
}
.cw-sign-dropdown::before {
  left: -10px;
  border-width: 10px 10px 10px 0;
  border-color: transparent rgba(20, 20, 40, 0.08) transparent transparent;
  filter: blur(0.5px);
}
.cw-sign-dropdown::after {
  left: -8px;
  border-width: 9px 9px 9px 0;
  border-color: transparent #ffffff transparent transparent;
}
/* Flipped: arrow points right */
.cw-sign-dropdown--flipped::before {
  left: auto;
  right: -10px;
  border-width: 10px 0 10px 10px;
  border-color: transparent transparent transparent rgba(20, 20, 40, 0.08);
}
.cw-sign-dropdown--flipped::after {
  left: auto;
  right: -8px;
  border-width: 9px 0 9px 9px;
  border-color: transparent transparent transparent #ffffff;
}

.cw-sign-option {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  font-size: 16px;
  color: #1a1a2e;
  background: none;
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: background 0.15s;
  font-weight: 500;
  position: relative;
}
.cw-sign-option:hover {
  background: #faf3f5;
}

.cw-sign-option-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f1ecff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6d28d9;
  flex-shrink: 0;
}
.cw-sign-option:hover .cw-sign-option-icon {
  background: #e6dfff;
}

.cw-sign-option-label {
  flex: 1;
}

.cw-sign-option-sep {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: -1px;
  height: 1px;
  background: #ececec;
}

/* Sign Contract footer button */
.cw-btn-sign {
  background: #1a1a2e;
  color: #fff;
  border: none;
  border-radius: 24px;
  padding: 9px 20px;
  font-size: 14px;
  font-weight: 600;
  margin-right: 10px;
  transition: background 0.15s;
}
.cw-btn-sign:hover:not(:disabled) {
  background: #2d2d4e;
}
.cw-btn-sign:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* VenueCrew sign button — teal variant */
.cw-btn-sign--vc {
  background: #0284c7;
}
.cw-btn-sign--vc:hover:not(:disabled) {
  background: #0369a1;
}

/* Typed signature preview */
.sig-type-preview {
  font-family: Georgia, 'Times New Roman', serif;
  font-style: italic;
  font-size: 2.2rem;
  color: #1a1a4e;
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fafafa;
  min-height: 60px;
  display: flex;
  align-items: center;
}

/* ── Venue Signature Modal ──────────────────────────────────────────── */
::v-deep .vsig-modal {
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
}
::v-deep .vsig-modal .el-dialog__header {
  display: none;
}
::v-deep .vsig-modal .el-dialog__body {
  padding: 28px 32px 24px;
  position: relative;
}

.vsig-close {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: background 0.15s;
  z-index: 2;
}
.vsig-close:hover {
  background: #f3f4f6;
}

.vsig-header {
  text-align: center;
  margin-bottom: 20px;
}
.vsig-title {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 24px;
  font-weight: 400;
  color: #1a1a2e;
  margin: 0 0 6px;
  letter-spacing: -0.01em;
}
.vsig-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.vsig-body {
  margin-bottom: 22px;
}

/* Draw */
.vsig-canvas-wrap {
  position: relative;
  background: #f7f7f9;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: center;
}
.vsig-canvas-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  color: #c1c1cf;
  pointer-events: none;
  letter-spacing: 0.02em;
}
.vsig-canvas {
  cursor: crosshair;
  background: #fff;
  border-radius: 8px;
  touch-action: none;
  display: block;
  margin: 0 auto;
}

/* Type */
.vsig-type-input-wrap {
  position: relative;
  background: #f7f7f9;
  border-radius: 12px;
  padding: 18px 22px;
  margin-bottom: 14px;
}
.vsig-type-input {
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  font-size: 32px;
  color: #1a1a2e;
  text-align: center;
  padding: 6px 30px;
}
.vsig-type-input::placeholder {
  color: #c1c1cf;
}
.vsig-type-edit {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 13px;
}
.vsig-font-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.vsig-font-chip {
  flex: 1;
  min-width: 0;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 16px;
  color: #1a1a2e;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}
.vsig-font-chip:hover {
  border-color: #c4b5fd;
}
.vsig-font-chip--active {
  border-color: #7c3aed;
  background: #faf7ff;
}

/* Upload */
.vsig-dropzone {
  background: #f7f7f9;
  border-radius: 12px;
  padding: 32px 16px;
  text-align: center;
  cursor: pointer;
  transition: background 0.15s;
}
.vsig-dropzone:hover {
  background: #f1efff;
}
.vsig-dropzone-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}
.vsig-dropzone-label {
  font-size: 13px;
  color: #4b5563;
  margin: 0 0 4px;
}
.vsig-dropzone-hint {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

.vsig-upload-preview {
  background: #cfd0d4;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  margin: 0 auto;
}
.vsig-upload-preview.vsig-panning {
  cursor: grabbing;
}
.vsig-upload-canvas {
  display: block;
  width: 100%;
  height: 100%;
}
.vsig-upload-img {
  max-width: 100%;
  max-height: 100%;
  border: 2px solid #3b82f6;
  background: #fff;
  transform-origin: center;
}
.vsig-zoom-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
}
.vsig-zoom-label {
  font-size: 13px;
  color: #4b5563;
  font-weight: 500;
}
.vsig-zoom-slider {
  flex: 1;
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  border-radius: 3px;
  outline: none;
  background: linear-gradient(to right,
    #7c3aed 0%,
    #7c3aed var(--vsig-zoom-pct, 50%),
    #e5e7eb var(--vsig-zoom-pct, 50%),
    #e5e7eb 100%);
}
.vsig-zoom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  background: #fff;
  border: 2px solid #7c3aed;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.vsig-zoom-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #fff;
  border: 2px solid #7c3aed;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}

/* Footer */
.vsig-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.vsig-link-btn {
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #4b5563;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s;
}
.vsig-link-btn:hover {
  background: #f3f4f6;
  border-color: #e5e7eb;
}
.vsig-link-btn i {
  margin-right: 6px;
}
.vsig-save-btn {
  background: #7c3aed;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 10px 22px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.vsig-save-btn:hover:not(:disabled) {
  background: #6d28d9;
}
.vsig-save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Venue select centering */
.cw-venue-select {
  display: flex;
  justify-content: center;
  padding: 16px 24px 0;
}

/* ── Party sign options (checkboxes in footer) ────────────────────── */
.cw-party-options {
  display: flex;
  gap: 16px;
  margin-top: 8px;
}

.cw-party-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.cw-party-checkbox input[type='checkbox'] {
  width: 14px;
  height: 14px;
  accent-color: #4c1d95;
  cursor: pointer;
  flex-shrink: 0;
}
</style>
